import { useContext } from 'react'

import {
  Button, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useProviderPriceList, { useProviderPriceListForm } from '@hooks/useProviderPriceList'

import EntitySelector from '@components/EntitySelector'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

const MODAL_KEY = 'BulkReassignAgenciesModal'

type ModalCallbacks = {
  bulkReassignAgencies: (payload: HandleCallbackActionParams) => void,
  closeModal: () => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const customRequiredFields = [
  { key: 'next_price_list_id' },
]

const BulkReassignAgenciesModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, bulkReassignAgencies } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { providerPriceList: initProviderPriceList } = modalPayload

  const {
    callbacks: { bulkReassignAgencies: bulkReassignFn },
    updating,
    providerPriceList,
    providerPriceList: {
      provider_id, service_id,
    },
  } = useProviderPriceList(initProviderPriceList)

  const providerPriceListFormPayload = useProviderPriceListForm(
    providerPriceList,
    { customRequiredFields, validateOn: 'change' },
  )

  const {
    entityState,
    entityState: { next_price_list_id },
    errors,
    saveEnabled,
    setEntityState,
  } = providerPriceListFormPayload

  const bulkReassignAgenciesPayload = {
    callbacks: {
      action: bulkReassignFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    requestOptions: {
      include_provider_price_list_next_price_list: true,
      include_provider_price_list_agency_providers: true,
    },
    toastText: 'Agencies Reassigned',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Bulk Reassign" titleSecondLine="Agencies" />

      <SidebarModal.Content>
        <EntitySelector
          callbacks={{
            selectItem: priceList => setEntityState({
              next_price_list_id: priceList ? priceList.id : null,
            }),
          }}
          entityKey="providerPriceLists"
          errorMessage={errors.next_price_list_id}
          label="Price List"
          searchRequestOptions={{
            provider_id,
            service_id,
          }}
          selectedIds={[providerPriceList.id]}
          selectedItemId={next_price_list_id}
        />
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={updating}
          onClick={() => bulkReassignAgencies(bulkReassignAgenciesPayload)}
          size="large"
        >
          {saveEnabled ? 'Reassign Agencies' : 'Select Price List'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <BulkReassignAgenciesModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
